import { useState } from "react";
import {
  Datagrid,
  List,
  TextField,
  BooleanInput,
  FunctionField,
  SelectInput,
  useSidebarState,
  useListContext,
  useDataProvider,
  TopToolbar,
} from "react-admin";
import {
  Box,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import PaginationOptions from "../../components/pagination-options";
import statsExporter from "./exporter";

// Custom ListActions with smart export (selected or all)
const ListActions = () => {
  const { selectedIds, filterValues, sort } = useListContext();
  const dataProvider = useDataProvider();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch all data from API with pagination to handle large datasets
      const pageSize = 1000;
      let allData = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const { data, total } = await dataProvider.getList("Stats", {
          pagination: { page, perPage: pageSize },
          sort: sort || { field: "firstName", order: "ASC" },
          filter: filterValues || {},
        });

        allData = [...allData, ...(data || [])];
        hasMore = allData.length < total;
        page++;
      }

      if (selectedIds && selectedIds.length > 0) {
        // Export only selected rows
        const selectedRecords = allData.filter((record) =>
          selectedIds.includes(record.id),
        );
        statsExporter(selectedRecords);
      } else {
        // Export all rows
        statsExporter(allData);
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <TopToolbar>
      <Button
        onClick={handleExport}
        disabled={exporting}
        startIcon={
          exporting ? <CircularProgress size={18} /> : <DownloadIcon />
        }
        size="small"
      >
        {selectedIds?.length > 0 ? `Export (${selectedIds.length})` : "Export"}
      </Button>
    </TopToolbar>
  );
};

const periodChoices = [
  { id: 30, name: "Last 30 days" },
  { id: 60, name: "Last 60 days" },
  { id: 90, name: "Last 90 days" },
  { id: 180, name: "Last 180 days" },
];

const StatsListFilter = [
  <SelectInput
    source="periodDays"
    label="Period"
    choices={periodChoices}
    variant="outlined"
    alwaysOn
    emptyText={false}
  />,
  <BooleanInput
    source="isSubscriber"
    label="Subscriber"
    alwaysOn
    sx={{ marginBottom: "10px", marginLeft: "4px" }}
  />,
];

const getDefaultFilters = () => {
  return { periodDays: 30, isSubscriber: true };
};

// Format age range: 0-18
const formatAgeRange = (min, max) => {
  if (min == null && max == null) return "-";
  return `${min ?? 0}-${max ?? 18}`;
};

// Format availability: Mon: All Day, Tues: All Day, etc.
// Collapsible Service & Rate component
const CollapsibleServiceRate = ({ services, prices }) => {
  const [expanded, setExpanded] = useState(false);

  if (!services || !Array.isArray(services) || services.length === 0)
    return "-";

  const serviceMap = {
    childcare: "CC",
    childcarePlus: "E",
    enrichment: "E",
    nightNurse: "NN",
    night_nurse: "NN",
    cleaning: "CL",
    cooking: "CK",
    housekeeping: "HK",
    virtual: "V",
    deepCleaning: "DC",
  };
  const priceKeyMap = {
    childcare: "childcare",
    childcarePlus: "childcarePlus",
    enrichment: "childcarePlus",
    nightNurse: "night_nurse",
    night_nurse: "night_nurse",
    cleaning: "cleaning",
    cooking: "cooking",
    housekeeping: "housekeeping",
    virtual: "virtual",
    deepCleaning: "deepCleaning",
  };

  const items = services
    .map((s) => {
      const code = serviceMap[s];
      if (!code) return null;
      const rate = prices && prices[priceKeyMap[s] || s];
      return rate ? `${code}-$${rate}` : code;
    })
    .filter(Boolean);

  if (items.length === 0) return "-";

  const fullText = items.join(", ");
  const maxLength = 30;
  const isLong = fullText.length > maxLength;

  if (!isLong) return fullText;

  const preview = fullText.substring(0, maxLength);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 150 }}>
      <Box sx={{ flex: 1 }}>{expanded ? fullText : `${preview}...`}</Box>
      <Tooltip title={expanded ? "Collapse" : "Expand"}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          sx={{ ml: 0.5, p: 0.25 }}
        >
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Collapsible Availability component
const CollapsibleAvailability = ({ schedule }) => {
  const [expanded, setExpanded] = useState(false);

  if (!schedule || !Array.isArray(schedule) || schedule.length === 0)
    return "-";

  const formatted = schedule
    .map((day) => {
      const dayName = day.weekday || "";
      if (!dayName) return null;
      const timeStr = day.allDay
        ? "All Day"
        : `${day.startTime || ""}-${day.endTime || ""}`;
      return `${dayName}: ${timeStr}`;
    })
    .filter(Boolean);

  if (formatted.length === 0) return "-";

  const fullText = formatted.join(", ");
  const maxLength = 30;
  const isLong = fullText.length > maxLength;

  if (!isLong) return fullText;

  const preview = fullText.substring(0, maxLength);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 150 }}>
      <Box sx={{ flex: 1 }}>{expanded ? fullText : `${preview}...`}</Box>
      <Tooltip title={expanded ? "Collapse" : "Expand"}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          sx={{ ml: 0.5, p: 0.25 }}
        >
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Collapsible Admin Notes component
const CollapsibleAdminNotes = ({ notes }) => {
  const [expanded, setExpanded] = useState(false);

  if (!notes) return "-";

  const maxLength = 30;
  const isLong = notes.length > maxLength;

  if (!isLong) return notes;

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 150 }}>
      <Box
        sx={{
          overflow: "hidden",
          textOverflow: expanded ? "unset" : "ellipsis",
          whiteSpace: expanded ? "pre-wrap" : "nowrap",
          flex: 1,
        }}
      >
        {expanded ? notes : `${notes.substring(0, maxLength)}...`}
      </Box>
      <Tooltip title={expanded ? "Collapse" : "Expand"}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          sx={{ ml: 0.5, p: 0.25 }}
        >
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Sidebar width when open
const SIDEBAR_WIDTH = 240;
const SIDEBAR_CLOSED_WIDTH = 55;
const PADDING = 48;

// Wrapper component for scrollable table with sticky first 2 columns (checkbox + name)
const ScrollableDatagridWrapper = ({ children, sidebarOpen }) => {
  const sidebarWidth = sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_CLOSED_WIDTH;

  return (
    <Box
      sx={{
        maxWidth: `calc(100vw - ${sidebarWidth + PADDING}px)`,
        overflowX: "auto",
        overflowY: "visible",
        position: "relative",
        "& .RaDatagrid-tableWrapper": {
          overflowX: "visible !important",
        },
        "& table": {
          minWidth: 2600,
          borderCollapse: "separate",
          borderSpacing: 0,
        },
        // First column (checkbox) - sticky
        "& thead th:nth-of-type(1), & tbody td:nth-of-type(1)": {
          position: "sticky",
          left: 0,
          zIndex: 2,
          width: 50,
          minWidth: 50,
          maxWidth: 50,
        },
        "& thead th:nth-of-type(1)": {
          backgroundColor: "#fafafa",
          zIndex: 3,
        },
        "& tbody td:nth-of-type(1)": {
          backgroundColor: "#fff",
        },
        // Second column (name) - sticky, positioned after checkbox
        "& thead th:nth-of-type(2), & tbody td:nth-of-type(2)": {
          position: "sticky",
          left: 50,
          zIndex: 2,
          minWidth: 180,
          boxShadow: "4px 0 8px -4px rgba(0,0,0,0.15)",
        },
        "& thead th:nth-of-type(2)": {
          backgroundColor: "#fafafa",
          zIndex: 3,
        },
        "& tbody td:nth-of-type(2)": {
          backgroundColor: "#fff",
        },
        // Hover states - solid color for sticky columns
        "& tbody tr:hover td:nth-of-type(1), & tbody tr:hover td:nth-of-type(2)":
          {
            backgroundColor: "#f5f5f5",
          },
      }}
    >
      {children}
    </Box>
  );
};

const listSx = {
  "& .RaList-content": {
    overflow: "hidden",
  },
  "& .RaList-main": {
    overflow: "hidden",
  },
};
const datagridSx = {
  "& .RaDatagrid-tableWrapper": {
    overflow: "visible",
  },
};

const StatsList = (props) => {
  const [sidebarOpen] = useSidebarState();

  return (
    <List
      {...props}
      storeKey="provider-statistics-list-v3"
      pagination={<PaginationOptions />}
      perPage={25}
      title="Provider Dashboard"
      filters={StatsListFilter}
      filterDefaultValues={getDefaultFilters()}
      disableSyncWithLocation={true}
      actions={<ListActions />}
      sx={listSx}
    >
      <ScrollableDatagridWrapper sidebarOpen={sidebarOpen}>
        <Datagrid sx={datagridSx}>
          {/* Name with link to provider - sticky column */}
          <FunctionField
            source="firstName"
            label="Name"
            render={(record) => (
              <a
                href={`/Provider/${record.id}/show`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record.firstName} {record.lastName}
              </a>
            )}
          />
          <TextField source="email" label="Email" />
          {/* Location: Zipcode, State */}
          <FunctionField
            label="Location"
            sx={{ minWidth: 100 }}
            render={(record) =>
              record.zipcode
                ? `${record.zipcode} ${record.state || ""}`.trim()
                : "-"
            }
          />
          {/* Match radius - convert from meters to miles */}
          <FunctionField
            label="Match Radius"
            render={(record) =>
              record.operationRadius
                ? `${(record.operationRadius / 1609.34).toFixed(1)} mi`
                : "-"
            }
          />
          <TextField
            source="subscriptionStartDate"
            label="Subscription Start"
          />
          <TextField source="activationDate" label="Activation Date" />
          <FunctionField
            source="reqRec"
            label="Req/Rec"
            cellClassName="percentage-cell"
            render={(record) => record.reqRec}
          />
          <TextField source="totalApiariSession" label="Total Sessions" />
          <TextField source="recommendations" label="Recommendations" />
          <TextField source="bookingsReceived" label="Bookings Received" />
          <TextField source="bookingsAccepted" label="Bookings Accepted" />
          <TextField source="interviewsReceived" label="Interviews Received" />
          <TextField source="interviewsAccepted" label="Interviews Accepted" />
          {/* Service & Rate - collapsible */}
          <FunctionField
            label="Service & Rate"
            sx={{ minWidth: 150 }}
            render={(record) => (
              <CollapsibleServiceRate
                services={record.services}
                prices={record.prices}
              />
            )}
          />
          {/* Reliability - round to 1 decimal place */}
          <FunctionField
            label="Reliability"
            render={(record) =>
              record.reliabilityScore != null
                ? Number(record.reliabilityScore).toFixed(1)
                : "-"
            }
          />
          {/* Age Range */}
          <FunctionField
            label="Age Range"
            render={(record) =>
              formatAgeRange(
                record.childcareAgeRangeMin,
                record.childcareAgeRangeMax,
              )
            }
          />
          {/* Availability - collapsible */}
          <FunctionField
            label="Availability"
            sx={{ minWidth: 150 }}
            render={(record) => (
              <CollapsibleAvailability schedule={record.regularSchedule} />
            )}
          />
          {/* Admin Notes - collapsible */}
          <FunctionField
            label="Admin Notes"
            sx={{ minWidth: 150 }}
            render={(record) => (
              <CollapsibleAdminNotes notes={record.adminNotes} />
            )}
          />
        </Datagrid>
      </ScrollableDatagridWrapper>
    </List>
  );
};

export default StatsList;
